import { FieldValues, UseFormTrigger } from 'react-hook-form';

import { Statuses } from './api';
import { TeaserBlock } from './teaser';
import {
  CartesiaEmotion,
  MiniMaxModel,
  STTProvider,
  TTSConfig,
  TTSProvider,
} from './voiceProviders';

export interface ZendeskDepartment {
  id: number;
  name: string;
  selected: boolean | undefined;
  enabled: boolean;
  ccapi_id?: string;
  description: string;
}

export type ZendeskConfig = Partial<{
  session_timeout: number;
  redirect_uri: string;
  access_token: string;
  refresh_token: string;
  scope: string;
  departments: ZendeskDepartment[];
  tags: string[];
  client_id: string;
  client_secret: string;
  subdomain: string;
  process_without_department: boolean;
}>;

export type SuncoConfig = Partial<{
  session_timeout: number;
  welcome_trigger_message: string;
  name: string;
  avatar: string;
  apikey_id: string;
  apikey_secret: string;
  webhook_secret: string;
  app_id: string;
  internal_integration_id: string;
  internal_integration_type: string;
  next_switchboard_id: string;
  create_ticket_on_session_expired: boolean;
  // oauth token
  access_token: string;
}>;

export type FrontConfig = Partial<{
  session_timeout: number;
  page_name: string;
  page_id: string;
  teammates: FrontTeammate[];
  teammate_id: string;
  refresh_token: string;

  // backwards compatible
  mode: 'agent' | 'system';
  sender_name: string;
  inboxes: FrontInbox[];
  tags: FrontTag[];
  tracked_tags: string[];
}>;

export type FrontTeammate = { id: string; name: string; status: Statuses };

export type FrontInbox = { id: number; name: string; selected?: boolean };

export type FrontTag = { id: string; name: string; highlight: string };

export interface FacebookPage {
  id: string;
  instagram_id?: string;
  access_token: string;
  name?: string;
  picture?: string;
  user_has_access?: boolean;
}

export type FacebookConfig = Partial<{
  session_timeout: number;
  access_token: string;
  verify_token: string;
  welcome_trigger_message: string;
  greeting_text: string;
  domains: string[];
  app_secret: string;
  page_id: string;
  public_customer_rsa_key: string;
  pages: FacebookPage[];
  page_name: string;
  is_manual: boolean;
  persistent_menu_options: string[];
  /** When true, a story reply or story mention that would start a new conversation is dropped. */
  ignore_story_interactions: boolean;
}>;

export type InstagramConfig = Partial<{
  session_timeout: number;
  access_token: string;
  verify_token: string;
  welcome_trigger_message: string;
  greeting_text: string;
  domains: string[];
  app_secret: string;
  page_id: string;
  public_customer_rsa_key: string;
  pages: FacebookPage[];
  page_name: string;
  is_manual: boolean;
  facebook_page_id: string;
  /** When true, a story reply or story mention that would start a new conversation is dropped. */
  ignore_story_interactions: boolean;
  // Same as WhatsApp coexistence: a human reply from the app pauses the AI Agent for that user.
  is_coexistence: boolean;
  coexistence_timeout: number;
  coexistence_reset_text: string;
}>;

export type IntercomConfig = Partial<{
  session_timeout: number;
  access_token: string;
  welcome_trigger_message: string;
  page_id: string;
  page_name: string;
  admin_id: string;
  admin_name: string;
}>;

/**
 * The admin pastes `client_id` and `client_secret`. The OAuth callback writes `refresh_token`,
 * `access_token` and `page_id` (the LiveChat organization id). integrations-controller writes
 * `author_id` (the bot), `bot_secret` and `webhook_secret`, and a new `access_token` on each verify.
 */
export type LiveChatConfig = Partial<{
  session_timeout: number;
  welcome_trigger_message: string;
  client_id: string;
  client_secret: string;
  refresh_token: string;
  access_token: string;
  page_id: string;
  author_id: string;
  bot_secret: string;
  webhook_secret: string;
}>;

export type LiveChatGroup = { id: number; name: string };

export type LiveChatBot = {
  name: string;
  avatar?: string;
  group_ids: number[];
  groups: LiveChatGroup[];
};

export type LiveChatBotProfile = Pick<LiveChatBot, 'name' | 'avatar'>;

export type LiveChatConnectError =
  'not_installed' | 'invalid_credentials' | 'not_authorized' | 'already_connected' | 'oauth_failed';

export type ViberConfig = Partial<{
  session_timeout: number;
  access_token: string;
  name: string;
  avatar: string;
  welcome_trigger_message: string;
  customer_rsa_key: string;
}>;

/**
 * MSTAT Viber Business configuration. Viber delivered through the MSTAT /
 * omni-messaging gateway rather than Viber's official Business API.
 *
 * Authentication is `api_token` XOR (`username` + `password`) — provide an API
 * token or HTTP Basic credentials, not both. `webhook_token` is the inbound
 * bearer token and `account_id` the MSTAT campaign account. Mirrors the
 * orchestrator `mstatViberIntegrationConfig` schema and the channels
 * `MSTATViberConfig` struct.
 */
export type MstatViberConfig = Partial<{
  session_timeout: number;
  webhook_token: string;
  api_token: string;
  username: string;
  password: string;
  account_id: number;
}>;

/**
 * Infobip SMS integration configuration which includes the apikey, base_url,
 * number, number_id and webhook_secret.
 */
export type InfobipSmsConfig = Partial<{
  session_timeout: number;
  apikey: string;
  base_url: string;
  number: string;
  number_id: string;
  webhook_secret: string;
}>;

export type InfobipRcsConfig = Partial<{
  session_timeout: number;
  apikey: string;
  base_url: string;
  sender_name: string;
  webhook_secret: string;
}>;

export type WhatsAppConfig = Partial<{
  session_timeout: number;
  access_token: string;
  phone_number_id?: string;
  display_phone_number?: string;
  prefilled_message?: string;
  include_agent_name?: boolean;
  test_numbers?: TestNumber[];
  // For whatsapp, page_id holds the phone_number_id (uniqueness/routing key);
  // waba_id carries the WhatsApp Business Account id. Un-migrated rows still
  // have the WABA in page_id and no waba_id — read the WABA as `waba_id ?? page_id`.
  page_id?: string;
  waba_id?: string;
  // Coexistence mode: allows using WhatsApp Business App and Cloud API simultaneously
  // Set during initial setup based on user's choice (existing business app vs new number)
  is_coexistence?: boolean;
  // Coexistence timeout in minutes - how long the bot waits before resuming after a human agent
  // replies via Business App. Default: 10080 (7 days)
  coexistence_timeout?: number;
  // Command text that human agents can send to hand back control to the bot. Default: '/reset'
  coexistence_reset_text?: string;
  public_customer_rsa_key?: string;
  list_button_text?: string;
}>;

export type SmartleadConfig = Partial<{ session_timeout: number; apikey: string }>;

export type SlackConfig = Partial<{
  session_timeout: number;
  signing_secret: string;
  access_token: string;
}>;

/**
 * Custom HTTP integration configuration for REST API-based integrations.
 */
export type CustomConfig = Partial<{
  session_timeout: number;
  webhook_url: string;
  webhook_secret: string;
  allowed_ips: string[];
  subscribed_events: string[];
  has_apikey: boolean;
}>;

export type EmailConfig = Partial<{
  session_timeout: number;
  from_email: string;
  from_name: string;
  apikey: string;
  apikey_id: string;
  domain_id: string;
  webhook_id: string;
  webhook_public_key: string;
  forwarding_verified: boolean;
  dns: { mail_cname: EmailDnsRecord; dkim1: EmailDnsRecord; dkim2: EmailDnsRecord };
}>;

export type EmailDnsRecord = Partial<{
  valid: boolean;
  type: string;
  host: string;
  data: string;
  reason: string;
}>;

export type TestNumber = { label: string; phone_number: string; rule_id?: string };

type WelcomeScreen = { enabled: boolean; greeting: string; starters: string[] };

export type PageTriggers = { [key: string]: { opening_message: string; trigger_message: string } };

export enum CustomFieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  DATE_TIME = 'date_time',
}

export type Field = {
  field: string;
  required: boolean;
  field_label?: string;
  variable?: string;
  variable_type?: CustomFieldType;
  languages?: string[];
};

type Disclaimer = { enabled: boolean; html: string };

type FormVisitorInformation = {
  headline: string;
  fields: Field[];
  disclaimer: Disclaimer;
  location: boolean;
};

type Launcher = {
  size: 'regular' | 'large';
  show: boolean;
  type: 'bubble' | 'bubble-sparkle' | 'help' | 'feedback' | 'question';
  icon_url: string;
  text: string;
};

export type WebWidgetConfig = Partial<{
  session_timeout: number;
  avatar: string;
  bubble_image: string;
  domains: string[];
  accent_color: string;
  background_color: string;
  welcome_message: string;
  welcome_trigger_message: string;
  /**
   * What the visitor sees beside the closed launcher, per URL and per section.
   *
   * Supersedes `welcome_message`: the console moves that line into
   * `teaser.message` the first time the launcher accordion is saved, and the
   * widget still falls back to `welcome_message` while it is absent.
   * `page_triggers[].opening_message` still outranks both on the URLs it matches.
   */
  teaser: TeaserBlock | null;
  /**
   * Paths where the widget shows. Absent, `null` or `[]` means every page. `*`
   * matches any run of characters, including `/`. The match uses only the path.
   */
  show_on_pages: string[] | null;
  /** Paths where the widget never shows, even in a live conversation. Wins over `show_on_pages`. */
  hide_on_pages: string[] | null;
  header_title: string;
  widget_position: string;
  widget_alignment: string;
  show_powered_by: boolean;
  window_size: string;
  icon_size: string;
  welcome_screen: WelcomeScreen;
  page_triggers: PageTriggers;
  visitor_information: FormVisitorInformation;
  launcher: Launcher;
  focus_trap: boolean;
  auto_open: boolean;
  theme: string;
  sticky_session: boolean;
  /** Minutes. Missing or `0` turns off the `defaultresumemessage` opener. */
  resume_timeout: number;
  inactivity_timeout: number;
  close_minimizes_window: boolean;
  show_survey?: boolean;
  generated_public_key: string;
  language?: string;
  public_customer_rsa_key: string;
  external_urls_limit: number;
  file_upload: boolean;
  /** Offers to keep the chat in a floating or small window when the visitor leaves the page. */
  floating_window?: boolean;
  rate_responses: boolean;
  /**
   * Optional pairing to a `web-voice` integration on the same desk. When set,
   * the webchat shows a voice call button. `null` clears the pairing. Liveness
   * is enforced at call time, so a disabled/removed pairing fails closed.
   */
  voice_integration_id: string | null;
}>;

export const enum IntegrationStatus {
  UNVERIFIED = 'unverified',
  VERIFIED = 'verified',
  VERIFYING = 'verifying',
  MISSING_CREDENTIALS = 'missing-credentials',
  MISSING_PERMISSIONS = 'missing-permissions',
  BAD_REQUEST = 'bad-request',
  INVALID_AUTHENTICATION = 'invalid-authentication',
  ACCOUNT_BLOCKED = 'account-blocked',
  NOT_FOUND = 'not-found',
  API_VERSION_NOT_SUPPORTED = 'api-version-not-supported',
  RATE_LIMIT = 'rate-limit',
  GENERAL_ERROR = 'general-error',
  FORBIDDEN = 'forbidden',
  INTERNAL_SERVER = 'internal-server',
}

export type Integration = {
  name: string;
  type: IntegrationType;
  integration_id: string;
  active: boolean;
  desk_id: string;
  created: string;
  updated: string;
  last_session_at: string;
  status?: IntegrationStatus;
  config: { session_timeout?: number; file_upload?: boolean; from_email?: string };
};

export enum IntegrationType {
  ZENDESK = 'zendesk',
  INTERCOM = 'intercom',
  LIVECHAT = 'livechat',
  FACEBOOK = 'facebook',
  VIBER = 'viber',
  MSTAT_VIBER = 'mstat-viber',
  WEB = 'web',
  SUNCO = 'sunco',
  INSTAGRAM = 'instagram',
  WHATSAPP = 'whatsapp',
  FRONT = 'front',
  INFOBIP_SMS = 'infobip-sms',
  INFOBIP_RCS = 'infobip-rcs',
  SMARTLEAD = 'smartlead',
  TWILIO_VOICE = 'twilio-voice',
  TELNYX_VOICE = 'telnyx-voice',
  AGORA_VOICE = 'agora-voice',
  WEB_VOICE = 'web-voice',
  EMAIL = 'email',
  SLACK = 'slack',
  CUSTOM = 'custom',
}

export interface ZendeskIntegration extends Integration {
  type: IntegrationType.ZENDESK;
  config: ZendeskConfig;
}

export interface IntercomIntegration extends Integration {
  type: IntegrationType.INTERCOM;
  config: IntercomConfig;
}

export interface LiveChatIntegration extends Integration {
  type: IntegrationType.LIVECHAT;
  config: LiveChatConfig;
}

export interface WebWidgetIntegration extends Integration {
  type: IntegrationType.WEB;
  config: WebWidgetConfig;
}

export interface SuncoIntegration extends Integration {
  type: IntegrationType.SUNCO;
  config: SuncoConfig;
}

export interface FacebookIntegration extends Integration {
  type: IntegrationType.FACEBOOK;
  config: FacebookConfig;
}

export interface InstagramIntegration extends Integration {
  type: IntegrationType.INSTAGRAM;
  config: InstagramConfig;
}

export interface ViberIntegration extends Integration {
  type: IntegrationType.VIBER;
  config: ViberConfig;
}

export interface MstatViberIntegration extends Integration {
  type: IntegrationType.MSTAT_VIBER;
  config: MstatViberConfig;
}

export interface WhatsAppIntegration extends Integration {
  type: IntegrationType.WHATSAPP;
  config: WhatsAppConfig;
}

export interface FrontIntegration extends Integration {
  type: IntegrationType.FRONT;
  config: FrontConfig;
}

export interface InfobipSmsIntegration extends Integration {
  type: IntegrationType.INFOBIP_SMS;
  config: InfobipSmsConfig;
}

export interface InfobipRcsIntegration extends Integration {
  type: IntegrationType.INFOBIP_RCS;
  config: InfobipRcsConfig;
}

export interface SmartleadIntegration extends Integration {
  type: IntegrationType.SMARTLEAD;
  config: SmartleadConfig;
}

export interface TwilioVoiceIntegration extends Integration {
  type: IntegrationType.TWILIO_VOICE;
  config: TwilioVoiceConfig;
}

export interface TelnyxVoiceIntegration extends Integration {
  type: IntegrationType.TELNYX_VOICE;
  config: TelnyxVoiceConfig;
}

export interface AgoraVoiceIntegration extends Integration {
  type: IntegrationType.AGORA_VOICE;
  config: AgoraVoiceConfig;
}

export interface WebVoiceIntegration extends Integration {
  type: IntegrationType.WEB_VOICE;
  config: WebVoiceConfig;
}

/** Shared type for components that work with any voice integration provider */
export type VoiceIntegration =
  TwilioVoiceIntegration | TelnyxVoiceIntegration | WebVoiceIntegration;

export interface EmailIntegration extends Integration {
  type: IntegrationType.EMAIL;
  config: EmailConfig;
}

export interface SlackIntegration extends Integration {
  type: IntegrationType.SLACK;
  config: SlackConfig;
}

export interface CustomIntegration extends Integration {
  type: IntegrationType.CUSTOM;
  config: CustomConfig;
}

export type Integrations = { integrations: Integration[] };

/**
 * IntegrationId is a type that is used to update an integration. Where
 * the integration_id and desk_id are required and the rest of the fields
 * are optional.
 */
export type IntegrationId<T extends Integration> = Partial<T> & {
  integration_id: string;
  desk_id: string;
};

export type NewIntegration<T extends Integration> = Partial<T> & {
  type: IntegrationType;
  desk_id: string;
};

export type FacebookError = { message: string; url: string };

export enum AccordionType {
  WEB_LAUNCHER_APPEARANCE = 'launcher-appearance',
  WEB_CHAT_APPEARANCE = 'webchat-appearance',
  WEB_CHAT_POSITION = 'webchat-position',
  WEB_CONNECT_AND_SECURE = 'connect-and-secure',
  WEB_VOICE = 'web-voice',

  WHATSAPP_TESTING = 'whatsapp-testing',
  WHATSAPP_PREFERENCE = 'whatsapp-preference',

  FACEBOOK_MANUAL_SETTINGS = 'facebook-manual-settings',

  FRONT_PREFERENCES = 'front-preferences',
  FRONT_MODE = 'front-mode',

  VIBER_CHAT_APPEARANCE = 'viber-chat-appearance',

  SUNCO_SETTINGS = 'sunshine-conversations-settings',

  // common
  VERIFICATION_STATUS = 'verification-status',
  CONNECTION_SETTINGS = 'connection-settings',
  ADVANCED_SETTINGS = 'advanced-settings',
  WHITELIST_DOMAINS = 'whitelist-domains',
  START_BEHAVIOR = 'start-behavior',
  VISITOR_INFORMATION = 'visitor-information',
}

export type AccordionState = { expanded: AccordionType | false; dirty: boolean };

export type AccordionFieldsetProps<Form extends FieldValues, T = Integration> = {
  type: AccordionType;
  integration?: T;
  toggleAccordion?: (panel: AccordionType) => (_event, isExpanded: boolean) => void;
  registerAccordion: (
    type: AccordionType,
    onSubmit?: () => void,
    triggerValidation?: UseFormTrigger<Form>
  ) => (el: HTMLDivElement) => void;
};

export interface WebFile {
  download_url: string;
  file_name: string;
  file_id: string;
  session_id: string;
}

/** Language preset selector. Language presets bundle recommended STT/TTS/noise settings. */
export type VoicePreset = 'en' | 'el' | 'pt-br' | 'custom';

/** Shared voice pipeline settings (STT/TTS, denoiser, recording). */
export interface BaseVoiceConfig {
  /** Active configuration preset; 'custom' means user-tuned advanced settings. */
  preset?: VoicePreset;
  /** Session timeout in seconds */
  session_timeout?: number;
  /** Phone number for outbound calls (telephony providers only) */
  phone_number?: string;
  /** Whether to record calls */
  call_recording?: boolean;
  /** Welcome message to trigger conversation */
  welcome_trigger_message?: string;

  /** Canonical language code (BrainLanguage value) — single source of truth for the UI */
  language?: string;

  /** STT provider identifier */
  stt_provider?: STTProvider;
  /** TTS provider identifier */
  tts_provider?: TTSProvider;
  /** Multi-provider TTS configuration (new unified config) */
  tts_config?: TTSConfig;

  /** Deepgram Speech-to-Text configuration */
  stt_deepgram?: STTDeepgramFullConfig;
  /** ElevenLabs Speech-to-Text configuration (Scribe v2 Realtime) */
  stt_elevenlabs?: STTElevenLabsConfig;
  /** Soniox Speech-to-Text configuration */
  stt_soniox?: STTSonioxConfig;
  /** ElevenLabs Text-to-Speech configuration */
  tts_elevenlabs?: TTSElevenLabsConfig;
  /** Cartesia Text-to-Speech configuration */
  tts_cartesia?: TTSCartesiaConfig;
  /** MiniMax Text-to-Speech configuration */
  tts_minimax?: TTSMiniMaxConfig;
  /** Thinking sound configuration (waiting/processing indicator) */
  thinking_sound?: {
    /** Name of the sound asset in S3 /media/sounds */
    name?: string;
  };
  /**
   * Noise suppression configuration. Base layer is Moveo-only;
   * TelnyxVoiceConfig widens this to support server-side Telnyx engines.
   */
  denoiser?: {
    enabled?: boolean;
    /** Provider discriminator. 'telnyx' is rejected at the base layer. */
    provider?: 'moveo';
    /** Per-session sidecar engine; required by the orchestrator when provider='moveo'. */
    moveo?: { engine?: 'gtcrn' | 'quail-voice-focus' };
  };
}

export interface TwilioVoiceConfig extends BaseVoiceConfig {
  /** Phone number for outbound calls */
  phone_number: string;
  /** Twilio Account SID */
  account_sid: string;
  /** Twilio Auth Token */
  auth_token: string;
  /** Twilio API Key (optional) */
  api_key?: string;
  /** Phone SID (optional) */
  phone_sid?: string;
  /** Whether to relay live transcripts to the Twilio Voice SDK client */
  transcript_relay_enabled?: boolean;
  /** Where an inbound SIP call carries the caller: "From" or an X- header name. Empty keeps the SIP From and fills no phone. */
  voice_psid_field?: string;
  /** Answering machine detection. Twilio dials on `enabled` alone — it has no mode selector. */
  amd?: { enabled: boolean };
}

// Must byte-match the orchestrator's telnyx-voice amd schema — it is the gatekeeper
// and rejects unknown modes. Single source for the TS type, yup schema, and the card UI.
export const AMD_MODES = ['premium', 'premium_ios_call_screening_detection'] as const;

export type AmdMode = (typeof AMD_MODES)[number];

/** Agora Conversational AI integration. NBA outbound dials from `phone_number`. */
export interface AgoraVoiceConfig {
  phone_number?: string;
  phone_number_id?: string;
  session_timeout?: number;
}

export interface TelnyxVoiceConfig extends Omit<BaseVoiceConfig, 'denoiser'> {
  /** Phone number for outbound calls */
  phone_number: string;
  /** Whether the dial-from number is Telnyx-issued or an externally verified caller ID. */
  phone_number_kind?: PhoneNumberKind;
  /** Telnyx phone resource ID. Auto-provisioned by integrations-controller during verify; never written by tooling. */
  phone_number_id?: string;
  /** Optional BYOC egress: customer SBC address (host / host:port / host:port;params). */
  egress_sip_address?: string;
  /** Optional BYOC egress SIP digest auth. Both-or-neither; blank clears. */
  egress_sip_auth_username?: string;
  egress_sip_auth_password?: string;
  /** Where an inbound SIP call carries the caller: "From" or an X- header name. Empty keeps the SIP From and fills no phone. */
  voice_psid_field?: string;
  /** Telnyx API Key */
  api_key: string;
  /** Telnyx Public Key (for webhook signature verification) */
  public_key?: string;
  /** Denoiser configuration — Telnyx widens provider to allow server-side. */
  denoiser?: {
    enabled?: boolean;
    provider?: 'moveo' | 'telnyx';
    moveo?: { engine?: 'gtcrn' | 'quail-voice-focus' };
    telnyx?: {
      engine?:
        | 'Denoiser'
        | 'DeepFilterNet'
        | 'Krisp Viva Tel'
        | 'Krisp Viva Tel Lite'
        | 'Krisp Viva Pro'
        | 'Krisp Viva SS'
        | 'AI-coustics Quail';
      direction?: 'inbound' | 'outbound' | 'both';
    };
  };
  /** Answering machine detection — Telnyx-only today; the orchestrator rejects `mode` on other voice providers. */
  amd?: { enabled: boolean; mode?: AmdMode };
}

export interface WebVoiceConfig extends BaseVoiceConfig {
  /** Origins permitted to initiate a web-voice call */
  allowed_origins: string[];
  /** WebRTC ICE transport policy — 'relay' forces TURN, 'all' allows direct P2P */
  ice_transport_policy?: 'relay' | 'all';
}

export const enum VoiceCredentialStatus {
  VERIFIED = 'verified',
  UNVERIFIED = 'unverified',
  INVALID = 'invalid',
  VALIDATING = 'validating',
}

/**
 * Configuration for Deepgram Speech-to-Text
 */
interface STTDeepgramFullConfig {
  /** Model to use for transcription */
  model?:
    | 'flux-general-en'
    | 'flux-general-multi'
    | 'nova-3'
    | 'nova-3-general'
    | 'nova-3-medical'
    | 'nova-2'
    | 'nova-2-general';
  /** Language code for transcription */
  language?: string;
  /** Keywords for boosting recognition (Nova 2 - format: "word:intensifier") */
  keywords?: string[];
  /** Key terms for boosting recognition (Flux, Nova 3) */
  keyterm?: string[];
  /** Endpointing threshold in milliseconds (Nova models) */
  endpointing?: number;
  /** Utterance end detection in milliseconds (Nova models) */
  utterance_end_ms?: number;
  /** End-of-turn detection threshold (Flux model, 0.5-0.9) */
  eot_threshold?: number;
  /** End-of-turn timeout in milliseconds (Flux model) */
  eot_timeout_ms?: number;
  /** Language hints for flux-general-multi */
  language_hint?: string[];
}

/**
 * Configuration for ElevenLabs Speech-to-Text (Scribe v2 Realtime)
 */
interface STTElevenLabsConfig {
  /** Model ID (e.g., "scribe_v2_realtime") */
  model_id?: string;
  /** Language code for transcription */
  language_code?: string;
  /** Commit strategy: "manual" or "vad" */
  commit_strategy?: 'manual' | 'vad';
  /** Silence duration before auto-commit in seconds (0-10) */
  vad_silence_threshold_secs?: number;
  /** VAD detection sensitivity (0-1) */
  vad_threshold?: number;
  /** Minimum speech duration in ms (0-5000) */
  min_speech_duration_ms?: number;
  /** Minimum silence duration in ms (0-5000) */
  min_silence_duration_ms?: number;
  /** Include word-level timestamps */
  include_timestamps?: boolean;
  /** Include language detection in committed transcripts */
  include_language_detection?: boolean;
  /** Enable logging (false = zero retention) */
  enable_logging?: boolean;
}

/**
 * Configuration for Soniox Speech-to-Text
 */
interface STTSonioxConfig {
  /** Model ID (e.g., "stt-rt-v4") */
  model?: string;
  /** Language hints for biasing recognition */
  language_hints?: string[];
  /** Restrict output to hinted languages */
  language_hints_strict?: boolean;
  /** Maximum delay before endpoint detection (500-3000ms) */
  max_endpoint_delay_ms?: number;
  /** Endpoint sensitivity (stt-rt-v5 only, -1.0 to 1.0) */
  endpoint_sensitivity?: number;
  /** Speech context for improved transcription */
  context?: {
    /** Background context text (max 10000 chars) */
    text?: string;
    /** Domain-specific terms for recognition (max 100 items) */
    terms?: string[];
  };
  /** Tag tokens with speaker labels */
  enable_speaker_diarization?: boolean;
  /** Channels-side filter for tokens by speaker; resolved server-side by default */
  speaker_policy?: 'primary_only' | 'all_speakers';
}

/**
 * Configuration for ElevenLabs Text-to-Speech
 */
interface TTSElevenLabsConfig {
  /** Customer's own ElevenLabs API key (BYO). Null clears the key. */
  api_key?: string | null;
  /** Data residency region for BYO accounts. */
  region?: 'global' | 'eu' | 'in';
  /** Voice ID to use */
  voice_id?: string;
  /** Model ID to use */
  model_id?: string;
  /** Language code */
  language_code?: string;
  /** Inactivity timeout in seconds */
  inactivity_timeout?: number;
  /** Whether to use auto mode */
  // Not used in the UI.
  auto_mode?: boolean;
  /** Text normalization setting */
  apply_text_normalization?: 'auto' | 'on' | 'off';
  /** Voice settings for fine-tuning */
  voice_settings?: {
    /** Stability setting (0-1) */
    stability?: number;
    /** Similarity boost setting (0-1) */
    similarity_boost?: number;
    /** Speed setting (0-1) */
    speed?: number;
  };
  /** ElevenLabs pronunciation dictionary locators */
  pronunciation_dictionary_locators?: {
    pronunciation_dictionary_id: string;
    version_id?: string;
  }[];
}

/**
 * Configuration for Cartesia Text-to-Speech
 */
interface TTSCartesiaConfig {
  /** Voice ID to use */
  voice_id?: string;
  /** Model ID to use */
  model_id?: string;
  /** Language code */
  language_code?: string;
  /** Volume setting (0-2, default 1.0) */
  volume?: number;
  /** Speed setting (0-2, default 1.0) */
  speed?: number;
  /** Emotion setting */
  emotion?: CartesiaEmotion;
  /** Cartesia pronunciation dictionary ID */
  pronunciation_dictionary_id?: string;
}

/**
 * Configuration for MiniMax Text-to-Speech.
 * Voices are multilingual; language is selected via language_boost (ISO code).
 */
interface TTSMiniMaxConfig {
  /** Voice ID to use */
  voice_id?: string;
  /** Model ID to use */
  model_id?: MiniMaxModel;
  /** Language boost (ISO code, mapped to MiniMax language name downstream) */
  language_boost?: string;
  /** Speed setting (0.5-2, default 1.0) */
  speed?: number;
  /** Volume setting (>0 to 10, default 1.0) */
  volume?: number;
  /** Pitch setting (-12 to 12, default 0) */
  pitch?: number;
  /** Normalize English text for better number/date reading */
  english_normalization?: boolean;
}

/**
 * Pronunciation dictionary responses returned by orchestrator
 * (`/voice/<provider>/dictionaries`). Server filters by account slug; the
 * payload carries at most one dict.
 */
export interface CartesiaDictionary {
  id: string;
  name: string;
  item_count: number;
  created_at: string;
}

export interface ElevenLabsDictionary {
  id: string;
  name: string;
  latest_version_id: string;
  rules_count: number;
  created_at: string;
}

export type CartesiaDictionaryResponse = { dictionary: CartesiaDictionary | null };

export type ElevenLabsDictionaryResponse = { dictionary: ElevenLabsDictionary | null };

/**
 * Voice data from Cartesia API (from orchestrator)
 */
export type CartesiaVoice = { id: string; name: string; description?: string; language?: string };

/**
 * Model data from Cartesia API (from orchestrator)
 */
export type CartesiaModel = { id: string; name: string };

export type TwilioPhoneNumber = {
  sid: string;
  account_sid: string;
  friendly_name: string;
  phone_number: string;
  voice_url: string;
  voice_method: string;
  voice_fallback_url: string;
  voice_fallback_method: string;
  voice_caller_id_lookup: boolean;
  date_created: string;
  date_updated: string;
  capabilities: { voice: boolean; sms: boolean; mms: boolean; fax: boolean };
  status_callback: string;
  status_callback_method: string;
  api_version: string;
  voice_receive_mode: string;
  origin: string;
  trunk_sid: string | null;
  emergency_status: string;
  emergency_address_sid: string | null;
  address_sid: string | null;
  identity_sid: string | null;
  bundle_sid: string | null;
  uri: string;
  /** Routing region from Twilio Routes API (e.g., 'us1', 'ie1'). Added by our backend, not from Twilio IncomingPhoneNumbers API. */
  voice_region?: string;
};

export type TelnyxPhoneNumber = { id: string; phone_number: string; connection_id: string };

export type TelnyxVerifiedNumber = {
  phone_number: string;
  record_type: string;
  verified_at: string;
};

export type PhoneNumberKind = 'native' | 'verified';

export type MergedTelnyxNumber = {
  phone_number: string;
  kind: PhoneNumberKind;
  id?: string;
  connection_id?: string;
  // True iff this number is already bound to another telnyx-voice integration.
  // Populated only for verified entries; native dedup uses connection_id.
  in_use?: boolean;
};

export type TwilioPhoneNumbersResponse = {
  incoming_phone_numbers: TwilioPhoneNumber[];
  page: number;
  page_size: number;
  num_pages: number;
  start: number;
  end: number;
  total: number;
  first_page_uri: string;
  next_page_uri: string | null;
  previous_page_uri: string | null;
  uri: string;
};
